import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Input } from 'baseui/input';
import { Select, SIZE } from 'baseui/select';
import { Button, KIND } from 'baseui/button';
import { Checkbox } from 'baseui/checkbox';
import { Tabs, Tab } from 'baseui/tabs-motion';
import Navbar from '../Items/Navbar';
import AuthContext from '../shared/AuthContext';
import PageMeta from '../seo/PageMeta';
import {
  getDailyReviewGoal,
  setDailyReviewGoal,
  getPracticeSource,
  setPracticeSource,
} from '../../utils/learningPreferences';
import {
  getNotificationPrefs,
  setNotificationPrefs,
} from '../../utils/notificationPreferences';
import SecuritySettingsPanel from '../settings/SecuritySettingsPanel';
import PlatformAdminUsersPanel from '../settings/PlatformAdminUsersPanel';
import { isPlatformAdmin } from '../../pages/teams/teamRoleUtils';
import { analyticsAccessForPlan, FEATURE } from '../../config/planFeatures';
import { useUserPlan } from '../../hooks/useUserPlan';
import PlanTierLabel from '../billing/PlanTierLabel';
import { usePlanGate } from '../../hooks/usePlanGate';
import GamificationSettingsPanel from '../settings/GamificationSettingsPanel';
import QuestionEditorPanel from '../settings/QuestionEditorPanel';
import { SETTINGS_TAB_MANAGE } from '../../config/settingsTabs';
import PlanBillingPanel from '../billing/PlanBillingPanel';
import { billingService } from '../shared/hooks/AuthService';
import useLiveLearningStats from '../../hooks/useLiveLearningStats';
import { formatLastSeen } from '../../utils/formatLastSeen';
import NavShortcutGrid, { NavShortcut } from '../navigation/NavShortcutGrid';
import '../navigation/appNav.css';
import '../settings/settings.css';

const practiceOptions = [
  { id: 'saved', label: 'Saved questions only' },
  { id: 'library', label: 'Random from library' },
];

const SETTINGS_TABS_OVERRIDES = {
  TabBar: { style: { width: '100%' } },
  TabList: {
    style: {
      background: '#eef2f6',
      borderRadius: '12px',
      padding: '5px',
      gap: '6px',
      marginBottom: 0,
      paddingBottom: '5px',
      border: '1px solid #e2e8f0',
    },
  },
  TabBorder: { style: { display: 'none', height: 0 } },
  TabHighlight: { style: { display: 'none', height: 0 } },
};

const SETTINGS_TAB_OVERRIDES = {
  Tab: {
    style: {
      background: 'transparent',
      borderRadius: '9px',
      paddingTop: '10px',
      paddingBottom: '10px',
      paddingLeft: '14px',
      paddingRight: '14px',
      fontWeight: 600,
      fontSize: '0.8125rem',
      color: '#64748b',
      flex: '0 0 auto',
      whiteSpace: 'nowrap',
      ':hover': { background: 'rgba(255, 255, 255, 0.55)' },
    },
    props: {
      className: 'settings-tab-btn',
    },
  },
};

const SettingsPage = () => {
  const { user, apiClient } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [activeKey, setActiveKey] = useState('0');
  const [profile, setProfile] = useState(() => user || JSON.parse(localStorage.getItem('userProfile') || 'null'));
  const [goal, setGoal] = useState(String(getDailyReviewGoal()));
  const [practiceSource, setPracticeSourceState] = useState(() => {
    const src = getPracticeSource();
    return practiceOptions.filter((o) => o.id === src);
  });
  const [notify, setNotify] = useState(getNotificationPrefs);
  const [usage, setUsage] = useState(null);
  const [savedMsg, setSavedMsg] = useState('');
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdError, setPwdError] = useState('');
  const live = useLiveLearningStats({ intervalMs: 30_000 });
  const { canUseFeature, planReady } = useUserPlan();
  const { blockUnlessFeature } = usePlanGate();
  const exportAllowed = planReady && canUseFeature(FEATURE.ANALYTICS_EXPORT);
  const analyticsTier = analyticsAccessForPlan(usage?.plan || 'FREE').tierLabel;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [meRes, usageRes] = await Promise.all([
          apiClient.get('/users/me'),
          apiClient.get('/api/users/me/usage'),
        ]);
        if (!cancelled) {
          if (meRes.data) {
            setProfile(meRes.data);
            localStorage.setItem('userProfile', JSON.stringify(meRes.data));
          }
          setUsage(usageRes.data);
        }
      } catch {
        /* offline cache ok */
      }
    })();
    return () => { cancelled = true; };
  }, [apiClient]);

  useEffect(() => {
    if (live.usage) setUsage(live.usage);
  }, [live.usage]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'billing') setActiveKey('3');
    if (tab === 'learning') setActiveKey('1');
    if (tab === 'security') setActiveKey('5');
    if (tab === 'data' || tab === 'privacy') setActiveKey('4');
    if (tab === SETTINGS_TAB_MANAGE || tab === 'questions') setActiveKey('7');
  }, [searchParams]);

  const flash = (msg) => {
    setSavedMsg(msg);
    setTimeout(() => setSavedMsg(''), 3500);
  };

  useEffect(() => {
    if (location.state?.blockedFeature) {
      flash('You need to upgrade your plan to use that feature.');
      setActiveKey('3');
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const saveLearningPrefs = async () => {
    const n = setDailyReviewGoal(goal);
    setGoal(String(n));
    const src = practiceSource[0]?.id || 'saved';
    setPracticeSource(src);
    try {
      await apiClient.patch('/api/users/me/learning-profile', {
        dailyGoal: n,
        onboardingCompleted: true,
      });
    } catch {
      /* local prefs still apply */
    }
    flash('Learning preferences saved. Practice deck will reload if open.');
  };

  const saveNotifications = () => {
    setNotificationPrefs(notify);
    flash('Notification preferences saved on this device.');
  };

  const changePassword = async () => {
    setPwdError('');
    if (pwd.next !== pwd.confirm) {
      setPwdError('New passwords do not match.');
      return;
    }
    try {
      await apiClient.post('/api/users/me/change-password', {
        currentPassword: pwd.current,
        newPassword: pwd.next,
      });
      setPwd({ current: '', next: '', confirm: '' });
      flash('Password updated.');
    } catch (err) {
      setPwdError(err.response?.data?.error || 'Could not update password.');
    }
  };

  const exportData = async () => {
    if (blockUnlessFeature(FEATURE.ANALYTICS_EXPORT)) {
      return;
    }
    if (!exportAllowed) {
      flash('You need to upgrade to Pro to export JSON.');
      setActiveKey('3');
      return;
    }
    try {
      const res = await apiClient.get('/api/users/me/export');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qnaread-export-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      flash('Export downloaded.');
    } catch {
      flash('Export failed. Try again when online.');
    }
  };

  const roles = profile?.roles || [];
  const plan = (usage?.plan || 'FREE').toUpperCase();
  const showAdminTab = isPlatformAdmin(profile);
  const profileInitial = useMemo(() => {
    const name = profile?.fullName || profile?.email || 'U';
    return String(name).trim().charAt(0).toUpperCase() || 'U';
  }, [profile?.fullName, profile?.email]);

  const aiUsed = usage?.aiUsedToday ?? 0;
  const aiLimit = usage?.aiDailyLimit ?? 0;
  const aiOverCap = usage && aiLimit > 0 && aiUsed > aiLimit;

  return (
    <div className="settings-page-outer qnaread-light-canvas">
      <PageMeta title="Settings" description="QnaRead account and learning preferences" />
      <Navbar userProfile={profile} loading={false} />
      <div className={`settings-page-shell${activeKey === '7' ? ' settings-page-shell--wide' : ''}`}>
        <div className="settings-hero">
          <header className="settings-header">
            <div className="settings-header-main">
              <h1>Settings</h1>
              <p className="settings-lead">Account, learning, billing, and security — synced with live study stats.</p>
            </div>
            <div className="settings-live-chip" aria-live="polite">
              <span className="settings-live-dot" data-pulse={live.refreshing ? 'true' : 'false'} />
              {live.updatedAt ? `Live · ${formatLastSeen(live.updatedAt.toISOString())}` : 'Syncing…'}
            </div>
          </header>
          <div className="settings-stat-row" aria-label="Study snapshot">
            <div className="settings-stat-item">
              <span className="settings-stat-label">Plan</span>
              <span className={`settings-plan-pill settings-plan-pill--${plan.toLowerCase()}`}>{plan}</span>
            </div>
            <div className="settings-stat-item">
              <span className="settings-stat-label">Due now</span>
              <span className="settings-stat-value">{live.dueCount}</span>
            </div>
            <div className="settings-stat-item">
              <span className="settings-stat-label">Saved</span>
              <span className="settings-stat-value">{live.savedCount}</span>
            </div>
            <div className={`settings-stat-item${aiOverCap ? ' settings-stat-item--warn' : ''}`}>
              <span className="settings-stat-label">AI today</span>
              <span className="settings-stat-value">
                {usage ? `${aiUsed} / ${aiLimit || '—'}` : '—'}
              </span>
            </div>
          </div>
        </div>

        {savedMsg && <p className="settings-flash" role="status">{savedMsg}</p>}

        <div className="settings-tabs-wrap">
        <Tabs
          activeKey={activeKey}
          onChange={({ activeKey: key }) => setActiveKey(key)}
          overrides={SETTINGS_TABS_OVERRIDES}
        >
          <Tab title="Profile" key="0" overrides={SETTINGS_TAB_OVERRIDES}>
            <div className="settings-panel">
              <section className="settings-card settings-profile-card">
                <div className="settings-profile-head">
                  <div className="settings-avatar" aria-hidden="true">{profileInitial}</div>
                  <div className="settings-profile-copy">
                    <h2>{profile?.fullName || 'User'}</h2>
                    <p className="settings-muted">{profile?.email}</p>
                  </div>
                </div>
                <dl className="settings-meta-list">
                  <div>
                    <dt>Plan</dt>
                    <dd><span className={`settings-plan-pill settings-plan-pill--${plan.toLowerCase()}`}>{plan}</span></dd>
                  </div>
                  <div>
                    <dt>Roles</dt>
                    <dd>{roles.join(', ') || 'ROLE_USER'}</dd>
                  </div>
                </dl>
              </section>
              <section className="settings-card">
                <h2>Change password</h2>
                <p className="settings-muted">Use at least 8 characters. MFA can be enabled under Security.</p>
                <div className="settings-form-stack">
                  <div className="settings-field">
                    <label htmlFor="pwd-current">Current password</label>
                    <Input id="pwd-current" value={pwd.current} onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))} placeholder="Current password" type="password" />
                  </div>
                  <div className="settings-field">
                    <label htmlFor="pwd-new">New password</label>
                    <Input id="pwd-new" value={pwd.next} onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))} placeholder="Min 8 characters" type="password" />
                  </div>
                  <div className="settings-field">
                    <label htmlFor="pwd-confirm">Confirm new password</label>
                    <Input id="pwd-confirm" value={pwd.confirm} onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))} placeholder="Repeat new password" type="password" />
                  </div>
                </div>
                {pwdError && <p className="settings-error">{pwdError}</p>}
                <div className="settings-actions">
                  <Button onClick={changePassword}>Update password</Button>
                </div>
              </section>
            </div>
          </Tab>
          <Tab title="Learning" key="1" overrides={SETTINGS_TAB_OVERRIDES}>
            <div className="settings-panel">
              <section className="settings-card">
                <h2>What you want to learn</h2>
                <p className="settings-muted">
                  Add catalog notebooks on Learn to unlock Library questions, or create a private custom notebook on
                  Learn.
                </p>
                <div className="settings-actions settings-actions--row">
                  <Link to="/settings/learning" className="settings-link-btn">
                    Manage learning interests
                  </Link>
                  <Link to="/hub#learn-library" className="settings-link-btn">
                    Library notebooks on Learn
                  </Link>
                </div>
              </section>
              <section className="settings-card">
                <h2>Practice &amp; review</h2>
                <p className="settings-muted">Changes apply immediately to Practice when that tab is open.</p>
                <div className="settings-form-stack settings-form-stack--narrow">
                  <div className="settings-field">
                    <label htmlFor="daily-goal">Daily review goal</label>
                    <Input id="daily-goal" type="number" min={1} max={200} value={goal} onChange={(e) => setGoal(e.target.value)} />
                  </div>
                  <div className="settings-field">
                    <label htmlFor="practice-source">Practice deck source</label>
                    <Select
                      inputId="practice-source"
                      options={practiceOptions}
                      value={practiceSource}
                      onChange={({ value }) => setPracticeSourceState(value)}
                      size={SIZE.compact}
                      clearable={false}
                    />
                  </div>
                </div>
                <div className="settings-actions">
                  <Button onClick={saveLearningPrefs}>Save learning preferences</Button>
                </div>
                <GamificationSettingsPanel onMessage={flash} embedded />
                <div className="settings-shortcuts-block">
                  <h3 className="settings-subheading">Quick links</h3>
                <NavShortcutGrid>
                  <NavShortcut to="/profile" label="My progress" description="Level, score & achievements" />
                  <NavShortcut
                    to="/leaderboard"
                    label="Leaderboard"
                    description="Weekly XP rankings"
                    planFeature={FEATURE.LEADERBOARD_RANK}
                  />
                  <NavShortcut to="/practice" label="Practice" description="Reveal & grade cards" />
                  <NavShortcut
                    to="/review"
                    label="Review"
                    description="Due spaced repetition"
                    badge={live.dueCount > 0 ? live.dueCount : undefined}
                    variant={live.dueCount > 0 ? 'primary' : 'secondary'}
                  />
                  <NavShortcut to="/saved" label="Saved" description="Bookmarked questions" />
                </NavShortcutGrid>
                </div>
              </section>
            </div>
          </Tab>
          <Tab title="Notifications" key="2" overrides={SETTINGS_TAB_OVERRIDES}>
            <div className="settings-panel">
              <section className="settings-card">
                <h2>Notifications</h2>
                <p className="settings-muted">Stored on this device until email delivery is enabled server-side.</p>
                <div className="settings-checklist">
                  <Checkbox checked={notify.revisionReminders} onChange={(e) => setNotify((n) => ({ ...n, revisionReminders: e.target.checked }))}>
                    Revision due reminders
                  </Checkbox>
                  <Checkbox checked={notify.weeklySummary} onChange={(e) => setNotify((n) => ({ ...n, weeklySummary: e.target.checked }))}>
                    Weekly learning summary
                  </Checkbox>
                  <Checkbox checked={notify.productUpdates} onChange={(e) => setNotify((n) => ({ ...n, productUpdates: e.target.checked }))}>
                    Product updates
                  </Checkbox>
                </div>
                <div className="settings-actions">
                  <Button onClick={saveNotifications}>Save notification preferences</Button>
                </div>
              </section>
            </div>
          </Tab>
          <Tab title="Plan & billing" key="3" overrides={SETTINGS_TAB_OVERRIDES}>
            <div className="settings-panel">
              <div className="settings-card settings-card--flush">
                <PlanBillingPanel usage={usage} currentPlan={usage?.plan} />
              </div>
              {(usage?.plan === 'PRO' || usage?.plan === 'TEAM') && (
                <div className="settings-actions settings-actions--standalone">
                  <Button
                    kind={KIND.secondary}
                    onClick={async () => {
                      try {
                        const res = await billingService.openPortal();
                        if (res.data?.url) window.location.href = res.data.url;
                      } catch {
                        flash('Billing portal requires Stripe customer setup.');
                      }
                    }}
                  >
                    Manage subscription in Stripe
                  </Button>
                </div>
              )}
            </div>
          </Tab>
          <Tab title="Security" key="5" overrides={SETTINGS_TAB_OVERRIDES}>
            <div className="settings-panel">
              <SecuritySettingsPanel onMessage={flash} />
            </div>
          </Tab>
          <Tab title="Data & privacy" key="4" overrides={SETTINGS_TAB_OVERRIDES}>
            <div className="settings-panel">
              <section className="settings-card">
                <h2>Export &amp; privacy</h2>
                <p className="settings-analytics-tier">
                  Analytics on your plan: <strong>{analyticsTier}</strong>
                </p>
                <p className="settings-muted">
                  {analyticsTier === 'Basic' && 'Upgrade for advanced charts on Study hub and JSON export below.'}
                  {analyticsTier === 'Advanced' && 'Includes JSON export. Team plan adds org-wide team analytics.'}
                  {analyticsTier === 'Advanced + team' && 'Includes JSON export and team workspace analytics.'}
                </p>
                <div className="settings-inline-actions">
                  <Button onClick={exportData}>Download my data (JSON)</Button>
                  {!exportAllowed && <PlanTierLabel tier="Pro" />}
                </div>
                {!exportAllowed && (
                  <p className="settings-muted settings-note">
                    JSON export applies on Pro and Team — we&apos;ll prompt you to upgrade if needed.{' '}
                    <Link to="/settings?tab=billing">Compare plans</Link>
                  </p>
                )}
                <p className="settings-muted settings-note">
                  To request account deletion, email <a href="mailto:support@qnaread.com">support@qnaread.com</a> from your registered address.
                </p>
                <p className="settings-legal-links">
                  <Link to="/privacy">Privacy</Link>
                  <span aria-hidden="true">·</span>
                  <Link to="/terms">Terms</Link>
                </p>
              </section>
            </div>
          </Tab>
          <Tab title="Manage" key="7" overrides={SETTINGS_TAB_OVERRIDES}>
            <div className="settings-panel settings-manage-panel">
              <section className="settings-card settings-card--flush">
                <QuestionEditorPanel embedded />
              </section>
            </div>
          </Tab>
          {showAdminTab && (
            <Tab title="Administration" key="6" overrides={SETTINGS_TAB_OVERRIDES}>
              <div className="settings-panel">
                <PlatformAdminUsersPanel />
              </div>
            </Tab>
          )}
        </Tabs>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
